import { SpanStatusCode } from '@opentelemetry/api';
import {
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_HTTP_ROUTE,
} from '@opentelemetry/semantic-conventions';
import * as Sentry from '@sentry/nextjs';
import Bubble from 'components/bubble';
import CTA from 'components/Campaign/CTA';
import { Drive } from 'components/cards';
import { Petition } from 'components/cards/petition';
import Counter from 'components/counter';
import Layout from 'components/layout';
import ShareBlock from 'components/Sharing';
import Tabs from 'components/tabs';
import Anchor from 'components/typography/anchor';
import NoUserContent from 'components/user/NoUserContent';
import userShareImg from 'images/shares/trending.png';
import { getPetitionsForUserCode } from 'lib/api/petitions';
import {
  getTrendingPetitionsV2,
  getUser,
  getUserContent,
} from 'lib/api/themis/queries';
import { otelApi } from 'lib/instrumentation'; // Initializes instrumentation
import {
  fieldsListView,
  PETITION_SIGNING_STATES,
  trimPetitions,
} from 'lib/petitions';
import { driveToUserContentSlim } from 'lib/transformers';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { DATA_TYPES } from 'siteconfig';
import { getOpenGraphUrl } from 'utilities/paths';
import { CampaignTypeDriveList, CampaignTypeList } from 'utilities/propTypes';

const BASE_PATH = 'go';

export const getServerSideProps = Sentry.wrapGetServerSidePropsWithSentry(
  async ({ params }) => {
    const parentCtx = otelApi.context.active();
    const parentSpan = otelApi.trace
      .getTracer('Gin Rickey')
      .startSpan('go/[usercode]', undefined, parentCtx);

    parentSpan.setAttributes({
      [ATTR_HTTP_ROUTE]: `/go/${params.usercode}`,
    });

    // For consistency reasons we jsut do this here.  Display of usernames is always lowercase (except the keyword bubbles)
    // and the cassini endpoint will normalize everything uppercase.
    const usercode = params.usercode.toLowerCase();

    // If the case was wrong on the sharecode, redirect to uppercased version
    // This should help SEO and reduce people sharing the wrong one
    if (usercode !== params.usercode) {
      return {
        redirect: {
          destination: `/go/${usercode}`,
          permanent: true,
        },
      };
    }

    const userPetitionsPromise = getPetitionsForUserCode({ usercode });
    const themisUserPromise = getUser({ code: usercode.toUpperCase() });

    const userResults = await Promise.all([
      userPetitionsPromise,
      themisUserPromise,
    ]);

    const user = userResults[0];
    const themisUser = userResults[1];

    // Failing to find the user code on petitions lookup implies the code is not found, so bail out
    if (user.notFound) {
      parentSpan.setStatus({
        code: SpanStatusCode.OK,
      });
      parentSpan.setAttributes({
        [ATTR_HTTP_RESPONSE_STATUS_CODE]: 404,
      });
      parentSpan.end();
      return {
        notFound: true,
      };
    }

    // Not great but E2E CI struggled to get past here with env vars not loading
    const themisUserContent = themisUser
      ? await getUserContent({
          userId: themisUser.id,
        })
      : {};
    const drivesFull = themisUserContent?.items || [];
    const trendingPetitions = await getTrendingPetitionsV2({
      range: 'SEVEN_DAY',
      limit: 12,
    });

    const open = [];
    const closed = [];
    let signCount = 0;

    const openDrives = [];
    const closedDrives = [];
    // Get slimmed drives and add their counts
    const drives = drivesFull.map((d) =>
      driveToUserContentSlim({ apiDrive: d })
    );
    drives.forEach((d) => {
      signCount += d.signCount;

      if (d.signStatus === PETITION_SIGNING_STATES.OPEN) {
        openDrives.push(d);
      } else {
        closedDrives.push(d);
      }
    });

    // Process each to add trending data and split into open/closed
    user.petitions.forEach((p) => {
      const sharecode = p.user_share_code;
      const isTrending = trendingPetitions.filter(
        (p) => p.petition_code === sharecode
      );

      // Move this to secondary level for if we ever use different time periods
      // This also happens in packages/site/src/lib/api/petitions/index.js
      p.trending = {
        sign_count_period: isTrending[0]?.trending?.sign_count_period || null,
      };

      // Add up all the total signatures they've gotten
      signCount = signCount + p.sign_count || 0;

      const is_closed = p.sign_status === PETITION_SIGNING_STATES.CLOSED;

      if (is_closed) {
        closed.push(p);
      } else {
        open.push(p);
      }
    });

    const slimOpen = trimPetitions({
      petitions: open,
      fieldsToKeep: fieldsListView,
    });
    const slimClosed = trimPetitions({
      petitions: closed,
      fieldsToKeep: fieldsListView,
    });

    parentSpan.end();
    return {
      props: {
        usercode,
        signCount,
        petitions: {
          open: slimOpen,
          closed: slimClosed,
        },
        drives: {
          open: openDrives,
          closed: closedDrives,
        },
        user: {
          followers: { count: themisUser?.follow?.followersCount || 0 },
          following: { count: themisUser?.follow?.followingCount || 0 },
        },
      },
    };
  }
);

const User = ({ usercode, petitions, signCount, drives, user }) => {
  const router = useRouter();
  const url = router.asPath;

  const dynamicOpenGraphUrlHref = getOpenGraphUrl('/api/og/user/user', {
    name: '', // we don't currently support this here
    usercode,
    totalSigns: signCount,
  });

  const hasNoContent =
    petitions.open.length === 0 &&
    petitions.closed.length === 0 &&
    drives.open.length === 0 &&
    drives.closed.length === 0;

  return (
    <Layout
      title={`${usercode} on Resistbot`}
      description={`Join the campaigns ${usercode} is running on Resistbot!`}
      image={userShareImg}
      dynamicImage={dynamicOpenGraphUrlHref}
      imageAltText={`Text Follow ${usercode} to 50409`}
      canonicalPath={`/${BASE_PATH}/${usercode.toLowerCase()}`}
    >
      <div className="mb-10 flex flex-wrap items-baseline justify-center gap-4 sm:justify-between">
        <h1>{usercode}</h1>
        <section className="flex flex-nowrap gap-4">
          <Counter count={user.followers.count} text="Followers" />
          <Counter count={signCount} text="Signers" />
        </section>
      </div>

      <div className="flex flex-col gap-6 sm:flex-row">
        <div className="grow">
          {hasNoContent ? (
            <NoUserContent />
          ) : (
            <>
              <CampaignTypeSection
                open={drives.open}
                closed={drives.closed}
                usercode={usercode}
                type={DATA_TYPES.DRIVE}
              />
              <CampaignTypeSection
                open={petitions.open}
                closed={petitions.closed}
                usercode={usercode}
                type={DATA_TYPES.PETITION}
              />
            </>
          )}
        </div>
        <div className="basis-80">
          <CTA
            copy={`Follow ${usercode}`}
            shareCode={usercode}
            type={DATA_TYPES.USER}
          />
          <p className="mb-4">
            Text <Bubble>Follow {usercode}</Bubble> to get a text when each
            campaign starts!
          </p>
          <p className="mb-4">
            Use the <Bubble>CLAIM</Bubble> keyword to get your own page,
            keyword, and to build a following on Resistbot. Followers{' '}
            <Anchor
              className="anchor-subtle"
              href="/news/2020/01/14/introducing-petitions"
            >
              receive texts for every campaign
            </Anchor>{' '}
            you create.
          </p>
          <ShareBlock
            pathname={url}
            text={`Text FOLLOW ${usercode} to 50409`}
          />
        </div>
      </div>
    </Layout>
  );
};

const CampaignTypeSection = ({ open, closed, usercode, type }) => {
  let headingText = 'UNKNOWN TYPE';
  let subText = 'UNKNOWN ACTION';
  switch (type) {
    case DATA_TYPES.PETITION:
      headingText = 'Petitions';
      subText =
        'To change laws and public policy at the federal, state, or local level.';
      break;
    case DATA_TYPES.DRIVE:
      headingText = 'Vote Drives';
      subText = 'To elect candidates up and down ballot.';
      break;
    default:
      break;
  }
  if (open.length > 0 || closed.length > 0) {
    return (
      <>
        <h2>{headingText}</h2>
        <p className="mb-4 text-sm">{subText}</p>
        <CampaignGroup
          closed={closed}
          open={open}
          usercode={usercode}
          type={type}
        />
      </>
    );
  }
  return null;
};

const CampaignGroup = ({ open, closed, usercode, type }) => {
  let keyPath;
  let humanType;
  switch (type) {
    case DATA_TYPES.PETITION:
      keyPath = 'petition_code';
      humanType = 'petition';
      break;
    case DATA_TYPES.DRIVE:
      keyPath = 'shareCode';
      humanType = 'drive';
      break;
    default:
      break;
  }
  const openGroup = open.map((campaign, idx) =>
    type === DATA_TYPES.DRIVE ? (
      <Drive key={campaign[keyPath]} drive={campaign} />
    ) : (
      <Petition key={campaign[keyPath]} petition={campaign} idx={idx} />
    )
  );
  const closedGroup = closed.map((campaign, idx) =>
    type === DATA_TYPES.DRIVE ? (
      <Drive key={campaign[keyPath]} drive={campaign} />
    ) : (
      <Petition key={campaign[keyPath]} petition={campaign} idx={idx} />
    )
  );

  const hasOpen = open.length > 0;
  const hasClosed = closed.length > 0;
  const hasBoth = hasOpen && hasClosed;
  const hasNone = !hasOpen && !hasClosed;

  const openTabText = hasOpen ? `Open (${open.length})` : `Open`;
  const closedTabText = hasClosed ? `Closed (${closed.length})` : `Closed`;

  // None of each
  if (hasNone) {
    return (
      <div className="card-flat p-10">
        Text <Bubble>Follow {usercode}</Bubble> to 50409 to receive a text when
        they launch their first campaign. Or see top{' '}
        <Anchor href="/petitions">petitions</Anchor> or{' '}
        <Anchor href="/drives">vote drives</Anchor> organized by other members.
      </div>
    );
  }
  // Some of both
  if (hasBoth) {
    return (
      <Tabs
        descriptor={`${humanType}`}
        searchDisabled={true}
        sections={[
          {
            contents: openGroup,
            title: openTabText,
          },
          {
            contents: closedGroup,
            title: closedTabText,
          },
        ]}
      ></Tabs>
    );
  }
  // Only open
  if (hasOpen && !hasClosed) {
    return openGroup;
  }
  // Only closed
  if (!hasOpen && hasClosed) {
    return (
      <Tabs
        descriptor={`${humanType}`}
        searchDisabled={true}
        sections={[
          {
            contents: openGroup,
            title: openTabText,
          },
          {
            contents: closedGroup,
            title: closedTabText,
          },
        ]}
      ></Tabs>
    );
  }
};

User.propTypes = {
  usercode: PropTypes.string.isRequired,
  petitions: PropTypes.exact({
    open: PropTypes.arrayOf(CampaignTypeList).isRequired,
    closed: PropTypes.arrayOf(CampaignTypeList).isRequired,
  }).isRequired,
  drives: PropTypes.exact({
    open: PropTypes.arrayOf(CampaignTypeDriveList).isRequired,
    closed: PropTypes.arrayOf(CampaignTypeDriveList).isRequired,
  }).isRequired,
  signCount: PropTypes.number.isRequired,
  user: PropTypes.exact({
    followers: PropTypes.exact({
      count: PropTypes.number.isRequired,
    }).isRequired,
    following: PropTypes.exact({
      count: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
};

export default User;
